import LoadingSpinner from '@/components/CustomComponent/LoadingSpinner';
import ImageMarker from '@/components/CustomComponent/ReactImageMarker';
import FollowButton from '@/components/communication/EmployeeLocations/FollowButton';
import {
  fetchEmployeeEvents,
  fetchEmployeeInfo,
  fetchEmployeeLocationByEmployeeId,
} from '@/components/communication/EmployeeLocations/query-request';
import { COMMON_TEXT } from '@/helpers/common-text';
import {
  MEMORY_STORE,
  QUERY_STRING_STORE,
  CHAT_TOOL_PRESENCE_COLOR,
} from '@/helpers/constants';
import EmployeeDetailMarkerRender from '@/helpers/employee-detail-marker';
import { transformPositionToMarkerCoordinate } from '@/helpers/utility';
import { format } from 'date-fns';
import { Button } from 'primereact/button';
import { Message } from 'primereact/message';
import { Toast } from 'primereact/toast';
import { Tooltip } from 'primereact/tooltip';
import PropTypes from 'prop-types';
import React, {
  Fragment,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import './EmployeeInfo.less';
import fileService from '@/services/file-service';
import { PCIcon } from './icons';
import { CopyIcon } from './icons';

const EmployeeInfo = props => {
  const copyToast = useRef(null);
  const querystring = useSelector(state => state.querystring);
  const sessionStore = useSelector(state => state.session);

  const [selectedBranchId, setSelectedBranchId] = useState(null);
  const [selectedTenantId, setSelectedTenantId] = useState(null);

  const [isLoadingEvents, setIsLoadingEvents] = useState(false);
  const [isLoadingLocation, setIsLoadingLocation] = useState(false);
  const [isLoadingInfo, setIsLoadingInfo] = useState(false);

  const [employeeInfo, setEmployeeInfo] = useState(null);
  const [employeeLocations, setEmployeeLocations] = useState(null);
  const [employeeEvents, setEmployeeEvents] = useState(null);
  const [isFollowing, setIsFollowing] = useState(false);

  const mapZoomRef = useRef(null);

  const { employeeId, followedEmployeeIds } = props;
  // Update isFollowing state when employeeId or followedEmployeeIds changes
  useEffect(() => {
    if (employeeId && followedEmployeeIds) {
      const following = followedEmployeeIds.has(parseInt(employeeId));
      setIsFollowing(following);
    }
  }, [employeeId, followedEmployeeIds]);

  const handleFollowChange = (employeeId, following) => {
    setIsFollowing(following);
    if (props.onFollowChange) {
      props.onFollowChange(employeeId, following);
    }
  };

  const showToast = (options) => {
    const toastRef = props.showToast || (copyToast.current && copyToast.current.show);
    if (toastRef) {
      if (typeof toastRef === 'function') {
        toastRef(options);
      } else {
        copyToast.current.show(options);
      }
    }
  };

  const getEmployeeInfo = useCallback(async ({ employeeId, tenantId }) => {
    setIsLoadingInfo(true);
    try {
      let employeeInfo = await fetchEmployeeInfo({
        employeeId,
        tenantId,
      });
      employeeInfo =
        employeeInfo && employeeInfo.length > 0 ? employeeInfo[0] : null;
      if (!employeeInfo) {
        setIsLoadingInfo(false);
        return;
      }
      let employeeImageUrl = 'assets/images/default_employee_avatar.jpg';
      const { employee_image } = employeeInfo;
      if (employee_image) {
        try {
          employeeImageUrl = await fileService.getFileUrl(employee_image);
        } catch (error) {
          console.error('Failed to get employee image URL:', error);
          // Keep default image on error
        }
      }
      // Ensure image URL is always set before updating state
      employeeInfo['employee_image_url'] = employeeImageUrl;
      setEmployeeInfo(employeeInfo);
    } catch (error) {
      console.error('Failed to fetch employee info:', error);
    } finally {
      setIsLoadingInfo(false);
    }
  }, []);

  const getEmployeeLocation = useCallback(
    async ({ employeeId, tenantId }) => {
      setIsLoadingLocation(true);
      let employeeLocations = await fetchEmployeeLocationByEmployeeId({
        employeeId,
        tenantId,
      });
      employeeLocations = employeeLocations ?? [];
      setEmployeeLocations(employeeLocations);
      setIsLoadingLocation(false);
    },
    []
  );

  const getEmployeeEvent = useCallback(async ({ employeeId, tenantId }) => {
    setIsLoadingEvents(true);
    const currentDate = new Date();
    const fromDate = format(currentDate, 'yyyy-MM-dd 00:00:00');
    const toDate = format(currentDate, 'yyyy-MM-dd 23:59:59');
    let employeeEvents = await fetchEmployeeEvents({
      employeeId,
      fromDate,
      toDate,
      tenantId,
    });
    employeeEvents = employeeEvents || [];
    employeeEvents.sort((a, b) => {
      if (a['allday'] && !b['allday']) return -1;
      if (!a['allday'] && b['allday']) return 1;
      return a['start_time'].localeCompare(b['start_time']);
    });
    employeeEvents.forEach(event => {
      event['start_time'] =
        format(new Date(event['start_time']), 'HH:mm') ?? '-';
      event['end_time'] = format(new Date(event['end_time']), 'HH:mm') ?? '-';
    });
    setEmployeeEvents(employeeEvents);
    setIsLoadingEvents(false);
  }, []);

  const copyTextToClipboard = async text => {
    await navigator.clipboard.writeText(text ?? '');
    copyToast.current.show({
      severity: 'success',
      detail: COMMON_TEXT.INFO_COPIED_TO_CLIPBOARD,
      life: 2000,
      content: props => (
        <div className="">
          <span>{props.message.detail}</span>
        </div>
      ),
    });
  };

  const componentUnmount = useCallback(() => { }, []);

  useEffect(() => {
    return componentUnmount;
  }, [componentUnmount]);

  useEffect(() => {
    setSelectedBranchId(querystring?.[QUERY_STRING_STORE.SELECT_BOX_BRANCH]?.[0]);
    setSelectedTenantId(
      querystring?.[QUERY_STRING_STORE.SELECT_BOX_TENANT]?.[0]
    );
  }, [querystring]);

  useEffect(() => {
    const { employeeId } = props;
    if (!employeeInfo && employeeId && selectedTenantId) {
      getEmployeeInfo({
        employeeId,
        tenantId: selectedTenantId,
      });
    }
  }, [selectedTenantId, getEmployeeInfo, props, employeeInfo]);

  useEffect(() => {
    const { employeeId } = props;
    if (employeeId && selectedTenantId && !employeeLocations) {
      getEmployeeLocation({
        employeeId,
        tenantId: selectedTenantId,
      });
    }
  }, [selectedTenantId, getEmployeeLocation, props, employeeLocations]);

  useEffect(() => {
    const { employeeId } = props;
    if (employeeEvents === null && employeeId && selectedTenantId) {
      getEmployeeEvent({
        employeeId,
        tenantId: selectedTenantId,
      });
    }
  }, [selectedTenantId, getEmployeeEvent, props, employeeEvents]);

  const EmployeeBasicInfo = () => {
    if (!employeeInfo) {
      return null;
    }
    const {
      employee_name,
      employee_name_reading,
      employee_image_url,
      url_teams: teamURL,
      current_branch_id: emplCurrentBranchId,
      employee_number,
      department,
      team,
      phone_number,
      phone_number2,
      email,
      chat_tool_presence,
      chat_tool_presence_color,
    } = employeeInfo;

    const employeePresence = +selectedBranchId === +emplCurrentBranchId;

    const basicInfoList = [
      { title: '社員番号', value: employee_number },
      { title: '部署', value: department },
      { title: 'チーム', value: team },
      {
        title: 'TEL1',
        value: phone_number,
        hasCopyButton: phone_number ? true : false,
        isTel: true,
      },
      {
        title: 'TEL2',
        value: phone_number2,
        hasCopyButton: phone_number2 ? true : false,
        isTel: true,
      },
      {
        title: 'Email',
        value: email,
        hasCopyButton: email ? true : false,
        isEmail: true,
      },
    ];

    return (
      <Fragment>
        {/* Section 1: Close Button */}
        <div className="close-button-section">
          <Button
            icon="pi pi-times"
            className="close-button"
            text
            onClick={() => {
              // This will be handled by the parent component (EmployeeLocationListRight)
              const event = new CustomEvent('closeSidebar');
              document.dispatchEvent(event);
            }}
          />
        </div>

        {/* Section 2: Header with Avatar, Name, Status */}
        <div className="employee-header-section">
          <div className="employee-avatar">
            <img src={employee_image_url} alt="employee" />
          </div>
          <div className="employee-info">
            <div className="employee-name">{employee_name}</div>
            {employee_name_reading && (
              <div className="employee-name-reading">{employee_name_reading}</div>
            )}
          </div>
          <div className="employee-status">
            <div className={`status-badge ${employeePresence === null ? 'status-default' : employeePresence ? 'present' : 'absent'}`}>
              {employeePresence === null ? '該当なし' : employeePresence ? '在室' : '不在'}
            </div>
            {chat_tool_presence != null && (
              <div className="chat-status">
                <div
                  className="chat-dot"
                  style={{
                    backgroundColor: CHAT_TOOL_PRESENCE_COLOR[chat_tool_presence_color?.toUpperCase()] || '#808080',
                  }}
                />
                <span className="chat-text">{chat_tool_presence}</span>
              </div>
            )}
          </div>
          <div className="employee-follow-button">
            <FollowButton
              employeeId={props.employeeId}
              isFollowing={isFollowing}
              onFollowChange={handleFollowChange}
              showToast={showToast}
              className="detail-follow-button"
            />
          </div>
          {teamURL && (
            <div className="teams-button">
              <Tooltip
                position="left"
                className="custom-marker-tooltip"
                target=".team-button"
                showDelay={0}
                hideDelay={0}
              >
                <span>{teamURL}</span>
              </Tooltip>
              <a
                href={teamURL}
                target="_blank"
                rel="noopener noreferrer"
                className="p-button team-button"
              >
                <img
                  alt="microsofteam"
                  src="assets/img/microsoft-team.png"
                />
              </a>
            </div>
          )}
        </div>


        {/* Mobile display */}
        <div className="employee-header-section-mobile">
          <div className="employee-header-section-mobile-row">
            <div className="employee-avatar">
              <img src={employee_image_url} alt="employee" />
            </div>
            <div className="employee-info">
              <div className="employee-name">{employee_name}</div>
              {employee_name_reading && (
                <div className="employee-name-reading">{employee_name_reading}</div>
              )}
            </div>
            {teamURL && (
              <div className="teams-button">
                <Tooltip
                  position="left"
                  className="custom-marker-tooltip"
                  target=".team-button"
                  showDelay={0}
                  hideDelay={0}
                >
                  <span>{teamURL}</span>
                </Tooltip>
                <a
                  href={teamURL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-button team-button"
                >
                  <img
                    alt="microsofteam"
                    src="assets/img/microsoft-team.png"
                  />
                </a>
              </div>
            )}
          </div>

          <div className="employee-header-section-mobile-row">
            <div className="employee-status">
              <div className={`status-badge ${employeePresence === null ? 'status-default' : employeePresence ? 'present' : 'absent'}`}>
                {employeePresence === null ? '該当なし' : employeePresence ? '在室' : '不在'}
              </div>
              {chat_tool_presence != null && (
                <div className="chat-status">
                  <div
                    className="chat-dot"
                    style={{
                      backgroundColor: CHAT_TOOL_PRESENCE_COLOR[chat_tool_presence_color?.toUpperCase()] || '#808080',
                    }}
                  />
                  <span className="chat-text">{chat_tool_presence}</span>
                </div>
              )}
            </div>
            <div className="employee-follow-button">
              <FollowButton
                employeeId={props.employeeId}
                isFollowing={isFollowing}
                onFollowChange={handleFollowChange}
                showToast={showToast}
                className="detail-follow-button"
              />
            </div>
          </div>

        </div>

        {/* Section 3: Employee Details */}
        <div className="employee-details-section">
          {basicInfoList.map((item, index) => {
            // Only render value and copy button if value is not empty
            if (!item.value) {
              return (
                <div className="detail-item" key={`detail-${index}`}>
                  <div className="detail-label">{item.title}</div>
                  <div className="detail-value"></div>
                </div>
              );
            }
            return (
              <div className="detail-item" key={`detail-${index}`}>
                <div className="detail-label">{item.title}</div>
                <div className="detail-value">
                  {item.isEmail && (
                    <a href={`mailto: ${item.value}`} className="detail-link">
                      {item.value}
                    </a>
                  )}
                  {item.isTel && (
                    <a href={`tel: ${item.value}`} className="detail-link">
                      {item.value}
                    </a>
                  )}
                  {!item.isEmail && !item.isTel && (
                    <span className="detail-text">{item.value}</span>
                  )}

                  {item.hasCopyButton && (
                    <button
                      className="copy-btn"
                      aria-label="copy-button"
                      onClick={() => copyTextToClipboard(item.value)}
                    >
                      <CopyIcon />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </Fragment>
    );
  };

  const EmployeeDetailLocation = () => {
    if (!employeeLocations || employeeLocations.length === 0 || !employeeInfo) {
      return <div className="no-location">{COMMON_TEXT.NO_LOCATION_DATA}</div>;
    }

    const LocationMapSection = ({ deviceType, markers }) => {
      const mapZoomRef = useRef(null);
      const deviceLabel = deviceType === 'pc' ? 'PC' : 'Mobile';
      return (
        <div className="location-info info-container">
          <div className="map-header">
            <div className="pc-icon-container">
              <PCIcon />
              <span className="pc-label">{deviceLabel}</span>
            </div>
            <div className="zoom-controls">
              <button
                className="zoom-button zoom-out"
                onClick={() => mapZoomRef.current && mapZoomRef.current.zoomOut()}
                type="button"
              >
                <img src="/assets/img/zoom-out-icon.svg" alt="Zoom Out" />
              </button>
              <button
                className="zoom-button zoom-fit"
                onClick={() => mapZoomRef.current && mapZoomRef.current.resetTransform()}
                type="button"
              >
                <img src="/assets/img/zoom-fit-icon.svg" alt="Zoom Fit" />
              </button>
              <button
                className="zoom-button zoom-in"
                onClick={() => mapZoomRef.current && mapZoomRef.current.zoomIn()}
                type="button"
              >
                <img src="/assets/img/zoom-in-icon.svg" alt="Zoom In" />
              </button>
            </div>
          </div>
          <div className="map-container">
            <LocationMap data={markers} mapZoomRef={mapZoomRef} />
          </div>
          {/* Location Data - below the map */}
          {markers && markers.length > 0 && (
            <div className="location-data">
              <div className="row-info">
                <div className="left-title">{`拠点`}</div>
                <div className="right-value">
                  <span>{markers[0].branch_name}</span>
                </div>
              </div>
              <div className="row-info">
                <div className="left-title">{`フロア`}</div>
                <div className="right-value">
                  <span>{markers[0].floor_name}</span>
                </div>
              </div>
              <div className="row-info">
                <div className="left-title">{`更新日時`}</div>
                <div className="right-value">
                  <span>
                    {markers[0].timestamp && format(new Date(markers[0].timestamp), 'MM月dd日 - HH:mm')}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      );
    };

    const LocationMap = ({ data, mapZoomRef }) => {
      if (!data || data.length === 0) {
        return (
          <div className="no-location">{COMMON_TEXT.NO_LOCATION_DATA}</div>
        );
      }
      const locationData = data[0];
      let markerList = [];
      const {
        position_x,
        position_y,
        floor_id,
        device_type,
        employee_device_name,
      } = locationData;

      // Use the employee image from the main employeeInfo state to ensure consistency
      // between the profile avatar and map marker
      // Wait for employeeInfo to be fully loaded before creating marker
      if (!employeeInfo || !employeeInfo.employee_image_url) {
        return <div className="no-location">{COMMON_TEXT.NO_LOCATION_DATA}</div>;
      }

      // Create marker data with correct device-specific information
      const employeeInfoForMarker = {
        ...locationData,
        employee_image_url: employeeInfo.employee_image_url,
        // Ensure device-specific information is preserved
        device_type: device_type,
        employee_device_name: employee_device_name || (device_type === 'pc' ? 'PC' : device_type === 'phone' ? 'Mobile' : 'Unknown'),
      };
      if (position_x && position_y) {
        const marker = transformPositionToMarkerCoordinate({
          x: position_x,
          y: position_y,
        });
        markerList = [
          {
            ...marker,
            item: employeeInfoForMarker || {},
          },
        ];
      }
      const floors = sessionStore[MEMORY_STORE.FLOORS] || [];
      const floor = floors.find(floor => +floor.floor_id === +floor_id);
      const floorMapUrl = floor?.floor_map_url;
      return (
        <Fragment>
          <div className="image-container">
            <ImageMarker
              ref={mapZoomRef}
              extraClass="custom-image-marker"
              bufferLeft={0}
              bufferTop={0}
              src={floorMapUrl}
              markerComponent={EmployeeDetailMarkerRender}
              markers={markerList}
              alt=""
            />
            <>
              {!floorMapUrl && (
                <Message
                  severity="error"
                  text={COMMON_TEXT.CANNOT_LOAD_FLOORMAP}
                />
              )}
            </>
          </div>
        </Fragment>
      );
    };

    let mobileDeviceMarkers = employeeLocations.filter(
      item => item.device_type === 'phone'
    );
    let pcDeviceMarkers = employeeLocations.filter(
      item => item.device_type === 'pc'
    );

    return (
      <Fragment>
        {pcDeviceMarkers.length > 0 && (
          <LocationMapSection deviceType="pc" markers={pcDeviceMarkers} />
        )}
        {mobileDeviceMarkers.length > 0 && (
          <LocationMapSection deviceType="mobile" markers={mobileDeviceMarkers} />
        )}
      </Fragment>
    );
  };

  const EmployeeDetailEvents = () => {
    if (isLoadingEvents) {
      return <LoadingSpinner />;
    }
    if (!employeeEvents || employeeEvents.length === 0) {
      return <div className="no-events">{COMMON_TEXT.EMPLOYEE_NO_EVENTS}</div>;
    }
    return (
      <Fragment>
        <Tooltip
          className="custom-marker-tooltip"
          target=".event-value"
          showDelay={0}
          hideDelay={0}
        ></Tooltip>
        {employeeEvents.map((event, index) => (
          <div className="row-info" key={`employee-event-${index}`}>
            <div className="left-title">
              {event['allday'] ? (
                <span>{'終日'}</span>
              ) : (
                <span style={{ fontFamily: 'monospace' }}>{`${event['start_time']} - ${event['end_time']}`}</span>
              )}
            </div>
            <div
              className="event-value right-value"
              data-pr-tooltip={`${event['start_time']} - ${event['end_time']} \n ${event['subject']} \n ${event['location']}`}
              data-pr-position="left"
            >
              <div className="subject-value">
                <span>{event['subject']}</span>
              </div>
              <div className="location-value">
                <span>{event['location']}</span>
              </div>
            </div>
          </div>
        ))}
      </Fragment>
    );
  };

  // Show main loading spinner when any critical data is loading
  if (isLoadingInfo || isLoadingLocation) {
    return (
      <Fragment>
        <Toast ref={copyToast} position="bottom-right" />
        <div className="employee-info-container">
          <LoadingSpinner />
        </div>
      </Fragment>
    );
  }

  return (
    <Fragment>
      <Toast ref={copyToast} position="bottom-right" />
      <div className="employee-info-container">
        <EmployeeBasicInfo></EmployeeBasicInfo>

        {/* Section 4: Map with PC Icon and Zoom */}
        <div className="location-info info-container">
          <div className="map-container">
            <EmployeeDetailLocation mapZoomRef={mapZoomRef} />
          </div>
        </div>

        {/* Section 5: Events */}
        <div className="events-info info-container">
          <div className="events-content">
            {employeeEvents && employeeEvents.length > 0 ? (
              <EmployeeDetailEvents></EmployeeDetailEvents>
            ) : (
              <div className="no-events">イベントなし</div>
            )}
          </div>
        </div>
      </div>
    </Fragment>
  );
};

EmployeeInfo.propTypes = {
  employeeId: PropTypes.number,
  deviceType: PropTypes.string,
  followedEmployeeIds: PropTypes.instanceOf(Set),
  onFollowChange: PropTypes.func,
  showToast: PropTypes.func,
};

export default EmployeeInfo;
